import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { tss } from 'tss-react/mui';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import SharedProductDetailsInput from './SharedProductDetailsInput';
import { KnowledgeBaseUploader } from './KnowledgeBaseUploader';
import { useTranslation } from 'react-i18next';
import { useSquadDeleteControllerExecuteMutation } from '@shared/services/apiService/apiService';
import CustomAlert from '@shared/components/alert/alert';
import { SquadIndicator } from './Squad/SquadIndicator';
import { SQUAD_MESSAGES } from '@features/Admin/Agent/constants/squad.constants';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';

const useStyles = tss.create(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
}));

interface AgentSectionProps {
  isSquad: boolean;
  squadName?: string | null;
  campaignId?: string;
  onSquadDeleted?: () => void;
}

const AgentSection: React.FC<AgentSectionProps> = ({
  isSquad,
  squadName,
  campaignId,
  onSquadDeleted,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const isDisabled = currentUser?.role === UserRolesEnum.VIEWER;

  const [deleteSquad, { isLoading: isDeleting }] = useSquadDeleteControllerExecuteMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [responseMsg, setResponseMsg] = useState<{ message: string; type: 'success' | 'error' }>({
    message: '',
    type: 'success',
  });

  const handleDeleteSquad = async () => {
    if (!campaignId) return;

    try {
      const response = await deleteSquad({ id: campaignId });

      if ('error' in response) {
        const errorMsg = t(SQUAD_MESSAGES.DELETED_ERROR);
        setResponseMsg({
          message: errorMsg,
          type: 'error',
        });
        toast.error((props) => <CustomErrorToast {...props} message={errorMsg} />, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        });
      } else {
        const successMsg = t(SQUAD_MESSAGES.DELETED_SUCCESS);
        setResponseMsg({
          message: successMsg,
          type: 'success',
        });
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('adminMainPage.squad.messages.success'),
            description: successMsg,
          },
        });
        setShowDeleteDialog(false);
        // Notify parent to refresh data
        if (onSquadDeleted) {
          onSquadDeleted();
        }
      }
    } catch {
      const errorMsg = t(SQUAD_MESSAGES.DELETED_ERROR);
      setResponseMsg({
        message: errorMsg,
        type: 'error',
      });
      toast.error((props) => <CustomErrorToast {...props} message={errorMsg} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    }
  };

  return (
    <Box className={classes.container}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h6">
            {isSquad && squadName
              ? `${t('adminMainPage.squad.labels.squadMode')}: ${squadName}`
              : t('adminMainPage.squad.descriptions.agentConfiguration')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSquad
              ? t('adminMainPage.squad.descriptions.squadConfig')
              : t('adminMainPage.squad.descriptions.singleAgentConfig')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SquadIndicator
            isSquad={isSquad}
            isDisabled={isDisabled}
            isDeleting={isDeleting}
            onDelete={() => setShowDeleteDialog(true)}
          />
        </Box>
      </Box>

      <SharedProductDetailsInput rows={4} />

      <KnowledgeBaseUploader campaignId={campaignId} isDisabled={isDisabled} />

      {responseMsg.message && <CustomAlert message={responseMsg.message} type={responseMsg.type} />}

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby="delete-squad-dialog-title"
        aria-describedby="delete-squad-dialog-description">
        <DialogTitle id="delete-squad-dialog-title">
          {t('adminMainPage.squad.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-squad-dialog-description">
            {t('adminMainPage.squad.deleteDialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
            {t('adminMainPage.operation.cancel')}
          </Button>
          <Button onClick={handleDeleteSquad} color="error" autoFocus disabled={isDeleting}>
            {isDeleting
              ? t('adminMainPage.operation.deleting')
              : t('adminMainPage.squad.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentSection;
